/**
 * Servicio de autenticación
 * Maneja el login, refresh y logout
 */

import axios, { AxiosInstance } from '@pankod/refine-simple-rest/node_modules/axios';
import { API_URL } from 'constants/environment';
import { StorageUtil } from 'utils/storage.util';
import { DeviceUtil } from 'utils/device.util';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // en segundos
  permissions?: string;
  defaultPage?: string;
  user?: any;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export class AuthService {
  private axiosInstance: AxiosInstance;
  private refreshPromise: Promise<RefreshResponse> | null = null;

  constructor(axiosInstance?: AxiosInstance) {
    this.axiosInstance = axiosInstance || axios.create({ baseURL: API_URL });
  }

  /**
   * Realizar login
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const deviceId = DeviceUtil.getOrCreateDeviceId();

    const response = await this.axiosInstance.post<LoginResponse>(
      `${API_URL}/auth/login`,
      {
        ...credentials,
        deviceId,
      }
    );

    const { accessToken, refreshToken, expiresIn, permissions, defaultPage, user } = response.data;

    // Guardar tokens y datos en localStorage
    StorageUtil.setAccessToken(accessToken);
    StorageUtil.setRefreshToken(refreshToken);
    StorageUtil.setTokenExpiry(expiresIn);

    if (permissions) {
      StorageUtil.setPermissions(permissions);
    }

    return response.data;
  }

  /**
   * Refresh token
   * Implementa singleton pattern para evitar múltiples refreshes simultáneos
   */
  async refreshToken(): Promise<RefreshResponse> {
    // Si ya hay un refresh en progreso, retornar la misma promesa
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = StorageUtil.getRefreshToken();
    const deviceId = DeviceUtil.getOrCreateDeviceId();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = this.performRefresh(refreshToken, deviceId);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Limpiar la promesa después de completar
      this.refreshPromise = null;
    }
  }

  private async performRefresh(refreshToken: string, deviceId: string): Promise<RefreshResponse> {
    try {
      const response = await this.axiosInstance.post<RefreshResponse>(
        `${API_URL}/auth/refresh`,
        {
          refreshToken,
          deviceId,
        }
      );

      const { accessToken, refreshToken: newRefreshToken, expiresIn } = response.data;

      // Actualizar tokens en localStorage
      StorageUtil.setAccessToken(accessToken);
      StorageUtil.setRefreshToken(newRefreshToken);
      StorageUtil.setTokenExpiry(expiresIn);

      return response.data;
    } catch (error) {
      // Si el refresh falla, limpiar tokens
      StorageUtil.clearTokens();
      throw error;
    }
  }

  /**
   * Logout
   * Invalida el refresh token en el backend
   */
  async logout(): Promise<void> {
    const refreshToken = StorageUtil.getRefreshToken();
    const deviceId = DeviceUtil.getOrCreateDeviceId();

    try {
      if (refreshToken) {
        // Intentar invalidar en el backend
        await this.axiosInstance.post(
          `${API_URL}/auth/logout`,
          {
            refreshToken,
            deviceId,
          }
        );
      }
    } catch (error) {
      // Ignorar errores del logout en backend
      console.error('Error during logout:', error);
    } finally {
      // Siempre limpiar tokens locales
      StorageUtil.clearTokens();
    }
  }

  /**
   * Obtener permisos del usuario actual
   */
  async getPermissions(): Promise<string[]> {
    const token = StorageUtil.getAccessToken();
    
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await this.axiosInstance.get<{ permissions: string }>(
      `${API_URL}/auth/permissions`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const permissions = response.data.permissions;
    StorageUtil.setPermissions(permissions);

    return permissions.split(',');
  }

  /**
   * Verificar si hay una sesión válida
   */
  hasValidSession(): boolean {
    return StorageUtil.hasValidSession();
  }

  /**
   * Verificar si el token necesita refresh preventivo
   */
  needsRefresh(): boolean {
    return StorageUtil.isTokenExpiringSoon(5); // 5 minutos antes
  }
}

// Instancia singleton
export const authService = new AuthService();
